/**
 * Shared formatting utilities for import detail modals.
 */

import { isCalendarDate } from '@dcp/shared'
import dayjs from '@dcp/shared/utils/dayjs'

/**
 * Convert an ISO date or datetime string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ)
 * to US display format (MM/DD/YYYY).
 *
 * Routes through `dayjs.utc()` rather than manual string splitting so the
 * codebase has *one* date-formatting strategy across both this surface and
 * the shared `formatDate` helper in `@dcp/shared/utils/formatters` (see
 * the module header there for the three-layer date strategy). Parsing the
 * calendar-date portion as UTC keeps the day stable across viewer
 * timezones — the same reason `formatDate` uses `dayjs.utc()` for its
 * DATE-column branch. The previous manual `split('-')` form worked
 * correctly but reading the codebase required understanding two
 * date-parsing approaches; one strategy is easier to maintain.
 *
 * Time-component handling: callers feed both calendar dates (`2024-05-11`)
 * AND ISO timestamps (`2024-05-11T10:30:00Z`). Trim the time portion via
 * `split(/[T ]/)[0]` before handing to dayjs — without this, the time zone
 * of a TIMESTAMPTZ value could shift the day forward/back in
 * negative-offset zones. After the trim the input is guaranteed to be a
 * pure `YYYY-MM-DD[Z]` (or invalid), and `dayjs.utc()` parses it
 * unambiguously as a calendar date in UTC.
 *
 * For *any* invalid input — null, undefined, or a non-empty string that
 * dayjs cannot parse — returns an empty string. Callers use this in
 * display table cells, where surfacing the raw unformatted value
 * (`'2024-13-45'`, `'garbage'`) would be more confusing than an empty
 * cell.
 *
 * @param isoDate - Date string in YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss[.SSS][Z]
 *                  format, or null/undefined
 * @returns Formatted date string in MM/DD/YYYY, or `''` for any invalid input
 */

export function formatYMDtoMDY(isoDate: string | null | undefined): string {
  if (!isoDate) return ''
  // Trim the time component if present — handles both `T`-separated ISO
  // timestamps and space-separated Postgres `timestamp` outputs. The
  // trailing `.replace(/Z$/, '')` strips a UTC marker that arrived
  // without a time portion (the rare but real `'YYYY-MM-DDZ'` form);
  // without it the regex shape check below would reject the otherwise-
  // valid input.
  const dateOnly = isoDate.split(/[T ]/)[0].replace(/Z$/, '')
  // `isCalendarDate` (@dcp/shared) is the product's shared answer to "is this a
  // real calendar day?" — bare `YYYY-MM-DD` shape (so lenient inputs like
  // `'2024-05'` or `'2024/05/11'` are rejected instead of coerced) plus a STRICT
  // parse. The strict tier matters: a plain `dayjs.utc(dateOnly).isValid()` is
  // TRUE for `'2024-02-30'`, `'2024-13-01'` and `'2023-02-29'`, because dayjs'
  // default parser rolls the overflow over — this helper used to return
  // `'03/01/2024'` for the first of those, i.e. a different day than the file
  // being imported actually contained.
  if (!isCalendarDate(dateOnly)) return ''
  // dayjs.utc keeps the calendar day stable across viewer timezones — same
  // strategy as `formatDate` in `@dcp/shared/utils/formatters` for its
  // DATE-column branch, so the codebase has one date-parsing approach across
  // both surfaces.
  return dayjs.utc(dateOnly).format('MM/DD/YYYY')
}
